// Webmail completo — cliente de correo (3 paneles: carpetas · lista · lector)
// en nuestro diseño, sobre /admin/webmail. Redactar, responder, reenviar,
// mover, borrar, adjuntos, búsqueda, paginación + overlay de prioridad IA.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertOctagon, ChevronLeft, ChevronRight, CornerUpLeft, Download, FileText,
  Forward, Inbox, Mail, MailOpen, Maximize2, Menu, MessagesSquare, Minimize2, Paperclip, Plus, RefreshCw, Search, Send,
  Sparkles, Star, Tag, Trash2, Users, X,
} from 'lucide-react';

import {
  deleteMessage, downloadAttachment, fetchTriageFor, getMessage, getStatus,
  listFolders, listMessages, moveMessage, setFlags, setLabel, type TriageInfo,
} from '../api';
import type { MailEnvelope, MailFolder, MailMessage } from '../types';
import { ComposeModal, type ComposePreset } from './ComposeModal';
import { ClientsPane, ThreadBox } from './ConversationPanels';
import { MessageBody } from './MessageBody';

const PAGE_SIZE = 30;

const FOLDER_ICON: Record<string, JSX.Element> = {
  inbox: <Inbox size={16} />, sent: <Send size={16} />, drafts: <FileText size={16} />,
  trash: <Trash2 size={16} />, junk: <AlertOctagon size={16} />, custom: <Mail size={16} />,
};

const IMP_BADGE: Record<string, { label: string; cls: string }> = {
  P1: { label: 'Urgente', cls: 'bg-red-100 text-red-700 border-red-300' },
  P2: { label: 'Normal', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  P3: { label: 'Bajo', cls: 'bg-gray-100 text-gray-500 border-gray-300' },
};

// Etiquetas sugeridas (el usuario puede crear las suyas). El agente las lee vía
// los mismos keywords IMAP. Color estable por nombre (hash) para reconocerlas.
const DEFAULT_LABELS = ['Cliente', 'Prospecto', 'Cotización', 'Urgente', 'Pagado', 'Seguimiento', 'Personal'];
const LABEL_COLORS = [
  'bg-blue-100 text-blue-700 border-blue-300',
  'bg-emerald-100 text-emerald-700 border-emerald-300',
  'bg-violet-100 text-violet-700 border-violet-300',
  'bg-rose-100 text-rose-700 border-rose-300',
  'bg-amber-100 text-amber-700 border-amber-300',
  'bg-cyan-100 text-cyan-700 border-cyan-300',
  'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300',
  'bg-teal-100 text-teal-700 border-teal-300',
];
function labelColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return LABEL_COLORS[h % LABEL_COLORS.length];
}

// Fecha compacta para la lista: hora si es hoy, "Ayer", día+mes si este año,
// con año si es más viejo.
function fmtDate(iso: string): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    const yest = new Date(now); yest.setDate(now.getDate() - 1);
    if (d.toDateString() === yest.toDateString()) return 'Ayer';
    const sameYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleDateString('es-MX', sameYear
      ? { day: '2-digit', month: 'short' }
      : { day: '2-digit', month: 'short', year: '2-digit' });
  } catch { return iso.slice(0, 10); }
}

// Fecha completa para el encabezado del lector (día, fecha y hora exacta).
function fmtFull(iso: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-MX', {
      weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function quote(m: MailMessage): string {
  const when = fmtDate(m.date);
  const who = m.from_name || m.from_addr;
  const src = (m.body_text || '').split('\n').map((l) => `> ${l}`).join('\n');
  return `\n\n\nEl ${when}, ${who} escribió:\n${src}`;
}

export function WebmailView() {
  const [folders, setFolders] = useState<MailFolder[]>([]);
  const [folder, setFolder] = useState('INBOX');
  const [list, setList] = useState<MailEnvelope[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loadingList, setLoadingList] = useState(true);
  const [selected, setSelected] = useState<MailMessage | null>(null);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [compose, setCompose] = useState<ComposePreset | null>(null);
  const [triage, setTriage] = useState<Record<string, TriageInfo>>({});
  const [moveOpen, setMoveOpen] = useState(false);
  const [foldersOpen, setFoldersOpen] = useState(false); // drawer de carpetas (móvil/tablet)
  const [labelFilter, setLabelFilter] = useState('');    // pestaña de etiqueta activa
  const [labelMenuOpen, setLabelMenuOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  // Lectura a ancho completo (oculta carpetas y lista en desktop). Preferencia
  // por usuario en este navegador; si el almacenamiento falla, arranca normal.
  const [wide, setWide] = useState<boolean>(() => {
    try { return localStorage.getItem('webmail.wideReader') === '1'; } catch { return false; }
  });
  const toggleWide = () => setWide((v) => {
    try { localStorage.setItem('webmail.wideReader', v ? '0' : '1'); } catch { /* noop */ }
    return !v;
  });
  const reading = wide && Boolean(selected);
  // Vista de la columna central: correos de la carpeta o "Clientes".
  const [view, setView] = useState<'mail' | 'clients'>('mail');
  // Agrupar por conversación (página actual del INBOX). Preferencia por navegador.
  const [grouped, setGrouped] = useState<boolean>(() => {
    try { return localStorage.getItem('webmail.groupThreads') === '1'; } catch { return false; }
  });
  const toggleGrouped = () => setGrouped((v) => {
    try { localStorage.setItem('webmail.groupThreads', v ? '0' : '1'); } catch { /* noop */ }
    return !v;
  });

  // Cada carga de lista invalida la IA pendiente de la anterior (cambio de
  // página/carpeta mientras el lookup sigue en vuelo).
  const listReqRef = useRef(0);
  // Último triage conocido: al refrescar se pinta con él (sin parpadeo) mientras
  // llega el nuevo.
  const triageRef = useRef<Record<string, TriageInfo>>({});

  const loadFolders = useCallback(async () => {
    try { setFolders((await listFolders()).folders); } catch { /* noop */ }
  }, []);

  const loadList = useCallback(async (f: string, p: number, q: string, silent = false, lbl = '') => {
    const req = ++listReqRef.current;
    if (!silent) { setLoadingList(true); setError(null); }
    try {
      const r = await listMessages(f, p, PAGE_SIZE, q, lbl);
      if (req !== listReqRef.current) return;
      // La lista se pinta YA; la IA llega después sin bloquearla. Sólo INBOX:
      // el triage indexa por UID del INBOX (los UID son por carpeta).
      const known = f === 'INBOX' ? triageRef.current : {};
      if (f !== 'INBOX') setTriage({});
      setList(r.messages.map((m) => ({ ...m, importance: known[m.uid]?.importance })));
      setTotal(r.total);
      setLoadingList(false);
      if (f === 'INBOX') {
        const tri = await fetchTriageFor(
          r.messages.map((m) => ({ uid: m.uid, from_addr: m.from_addr, subject: m.subject })),
        );
        if (req !== listReqRef.current) return;
        triageRef.current = { ...triageRef.current, ...tri };
        setTriage(triageRef.current);
        setList((prev) => prev.map((m) => ({ ...m, importance: tri[m.uid]?.importance })));
      }
    } catch (e: any) {
      if (req === listReqRef.current) { setError(e?.message || 'No se pudo cargar la carpeta.'); setList([]); }
    } finally { if (req === listReqRef.current) setLoadingList(false); }
  }, []);

  useEffect(() => { void loadFolders(); }, [loadFolders]);
  useEffect(() => { void loadList(folder, page, search, false, labelFilter); /* eslint-disable-next-line */ }, [folder, page]);

  // Tiempo real GENTIL (Fase 3): cada 20s, con la pestaña visible y sin redactar,
  // un chequeo LIGERO (1 STATUS IMAP, sin fetch) de la carpeta actual. SOLO si
  // uidnext/mensajes/no-leídos cambiaron hacemos el refresh completo → los
  // correos nuevos aparecen casi al instante sin martillar el servidor (nada de
  // IDLE persistente ni polling pesado → cero riesgo de ban).
  const liveRef = useRef({ folder, page, search, labelFilter, composing: false });
  liveRef.current = { folder, page, search, labelFilter, composing: Boolean(compose) };
  const lastStatusRef = useRef<{ uidnext: number; messages: number; unseen: number } | null>(null);
  useEffect(() => { lastStatusRef.current = null; }, [folder]); // baseline al cambiar de carpeta
  useEffect(() => {
    const id = setInterval(async () => {
      const s = liveRef.current;
      if (s.composing || document.hidden || s.search) return;
      try {
        const st = await getStatus(s.folder);
        const prev = lastStatusRef.current;
        lastStatusRef.current = { uidnext: st.uidnext, messages: st.messages, unseen: st.unseen };
        if (prev && (prev.uidnext !== st.uidnext || prev.messages !== st.messages || prev.unseen !== st.unseen)) {
          void loadFolders();
          if (s.page === 1) void loadList(s.folder, 1, s.search, true, s.labelFilter);
        }
      } catch { /* silencioso: si falla, seguimos sin refrescar */ }
    }, 20000);
    return () => clearInterval(id);
  }, [loadFolders, loadList]);

  const openMessage = async (env: MailEnvelope) => {
    setLoadingMsg(true); setSelected(null); setMoveOpen(false); setLabelMenuOpen(false);
    try {
      const msg = await getMessage(folder, env.uid, folder === 'INBOX');
      setSelected(msg);
      if (!env.seen) {
        setList((prev) => prev.map((m) => (m.uid === env.uid ? { ...m, seen: true } : m)));
        setFolders((prev) => prev.map((f) => (f.name === folder ? { ...f, unread: Math.max(0, f.unread - 1) } : f)));
      }
    } catch (e: any) { setError(e?.message || 'No se pudo abrir el mensaje.'); }
    finally { setLoadingMsg(false); }
  };

  // Abre un correo del INBOX por UID (desde la conversación o la vista Clientes).
  const openUid = async (uid: string) => {
    setLoadingMsg(true); setSelected(null); setMoveOpen(false); setLabelMenuOpen(false);
    try {
      const msg = await getMessage('INBOX', uid, true);
      setSelected(msg);
      if (!triageRef.current[uid]) {           // puede no estar en la página cargada
        const tri = await fetchTriageFor([{ uid, from_addr: msg.from_addr, subject: msg.subject }]);
        triageRef.current = { ...triageRef.current, ...tri };
        setTriage(triageRef.current);
      }
    } catch (e: any) { setError(e?.message || 'No se pudo abrir el mensaje (¿se movió de la bandeja?).'); }
    finally { setLoadingMsg(false); }
  };

  // Filas visibles: con "agrupar", una por conversación (la más reciente).
  const rows = useMemo(() => {
    if (!grouped || folder !== 'INBOX') return list;
    const seen = new Set<string>();
    return list.filter((m) => {
      const k = triage[m.uid]?.threadKey;
      if (!k) return true;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [grouped, folder, list, triage]);

  const refresh = () => { void loadFolders(); void loadList(folder, page, search, false, labelFilter); };

  const doSearch = () => { setPage(1); void loadList(folder, 1, search, false, labelFilter); };

  // Cambiar de pestaña de etiqueta: filtra server-side (SEARCH KEYWORD) y va a p.1.
  const selectLabel = (lbl: string) => {
    const next = labelFilter === lbl ? '' : lbl;
    setLabelFilter(next); setPage(1); setSelected(null);
    void loadList(folder, 1, search, false, next);
  };

  const onDelete = async (env: MailEnvelope) => {
    try { await deleteMessage(folder, env.uid); setSelected(null); refresh(); }
    catch (e: any) { setError(e?.message || 'No se pudo borrar.'); }
  };
  const onMove = async (dest: string) => {
    if (!selected) return;
    try { await moveMessage(folder, selected.uid, dest); setSelected(null); setMoveOpen(false); refresh(); }
    catch (e: any) { setError(e?.message || 'No se pudo mover.'); }
  };
  const onStar = async (env: MailEnvelope) => {
    const next = !env.flagged;
    setList((prev) => prev.map((m) => (m.uid === env.uid ? { ...m, flagged: next } : m)));
    try { await setFlags(folder, env.uid, { flagged: next }); } catch { refresh(); }
  };
  const onMarkUnread = async (env: MailEnvelope) => {
    setList((prev) => prev.map((m) => (m.uid === env.uid ? { ...m, seen: false } : m)));
    try { await setFlags(folder, env.uid, { seen: false }); setSelected(null); } catch { refresh(); }
  };
  // SPAM sin tokens: solo mover a la carpeta Spam del servidor (o de vuelta a
  // la bandeja). El servidor ya clasifica spam; esto es el control del usuario.
  const onSpam = async () => {
    const junk = folders.find((f) => f.role === 'junk')?.name;
    if (!selected || !junk) return;
    try { await moveMessage(folder, selected.uid, junk); setSelected(null); refresh(); }
    catch (e: any) { setError(e?.message || 'No se pudo marcar como spam.'); }
  };
  const onNotSpam = async () => {
    const inbox = folders.find((f) => f.role === 'inbox')?.name ?? 'INBOX';
    if (!selected) return;
    try { await moveMessage(folder, selected.uid, inbox); setSelected(null); refresh(); }
    catch (e: any) { setError(e?.message || 'No se pudo restaurar el correo.'); }
  };
  // Poner/quitar etiqueta al correo abierto (keyword IMAP, se sincroniza).
  const onLabelToggle = async (labelName: string, add: boolean) => {
    if (!selected) return;
    const name = labelName.trim();
    if (!name) return;
    try {
      const r = await setLabel(folder, selected.uid, name, add);
      setSelected((prev) => (prev ? { ...prev, labels: r.labels } : prev));
      setList((prev) => prev.map((m) => (m.uid === selected.uid ? { ...m, labels: r.labels } : m)));
      setNewLabel('');
    } catch (e: any) { setError(e?.message || 'No se pudo actualizar la etiqueta.'); }
  };

  const currentRole = folders.find((f) => f.name === folder)?.role;
  const hasJunk = folders.some((f) => f.role === 'junk');

  // Pestañas de etiqueta = las que ya existen en la carpeta (unión sobre la lista).
  const availableLabels = useMemo(() => {
    const s = new Set<string>();
    for (const m of list) for (const l of m.labels ?? []) s.add(l);
    if (labelFilter) s.add(labelFilter);
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'es'));
  }, [list, labelFilter]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="flex h-[calc(100dvh-3.5rem)] overflow-hidden bg-background">
      {/* ── Carpetas: rail fijo en desktop, drawer deslizable en móvil/tablet ── */}
      {foldersOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 backdrop-blur-[1px] md:hidden" onClick={() => setFoldersOpen(false)} aria-hidden />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-border bg-card transition-transform duration-200 ease-out md:static md:z-auto md:w-48 md:translate-x-0 md:shadow-none xl:w-52 ${reading ? 'md:hidden' : ''} ${
          foldersOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}`}
      >
        <div className="flex items-center justify-between px-3 pt-3 md:hidden">
          <span className="text-sm font-semibold text-foreground">Carpetas</span>
          <button onClick={() => setFoldersOpen(false)} className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted" aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <button onClick={() => { setCompose({}); setFoldersOpen(false); }}
          className="m-3 inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-3 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 active:scale-[.98] transition">
          <Send size={15} /> Redactar
        </button>
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 pb-3">
          {folders.map((f) => (
            <button key={f.name} onClick={() => { setView('mail'); setFolder(f.name); setPage(1); setSelected(null); setSearch(''); setLabelFilter(''); setFoldersOpen(false); }}
              className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2.5 text-sm ${
                view === 'mail' && folder === f.name ? 'bg-primary/10 font-medium text-primary' : 'text-foreground hover:bg-muted'}`}>
              <span className={folder === f.name ? 'text-primary' : 'text-muted-foreground'}>
                {FOLDER_ICON[f.role] ?? FOLDER_ICON.custom}
              </span>
              <span className="flex-1 truncate text-left">{f.label}</span>
              {f.unread > 0 && (
                <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">{f.unread}</span>
              )}
            </button>
          ))}
          <button onClick={() => { setView('clients'); setSelected(null); setFoldersOpen(false); }}
            className={`mt-2 flex w-full items-center gap-2 rounded-lg border-t border-border px-2.5 py-2.5 text-sm ${
              view === 'clients' ? 'bg-primary/10 font-medium text-primary' : 'text-foreground hover:bg-muted'}`}>
            <span className={view === 'clients' ? 'text-primary' : 'text-muted-foreground'}><Users size={16} /></span>
            <span className="flex-1 truncate text-left">Clientes</span>
          </button>
        </nav>
      </aside>

      {/* ── Lista ── */}
      <section className={`${selected ? 'hidden md:flex' : 'flex'} ${reading ? 'md:hidden' : ''} w-full flex-col border-r border-border md:w-80 xl:w-96`}>
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          {/* hamburguesa → drawer de carpetas (móvil/tablet) */}
          <button onClick={() => setFoldersOpen(true)}
            className="rounded-lg border border-border bg-card p-1.5 text-foreground hover:bg-muted md:hidden" aria-label="Carpetas">
            <Menu size={16} />
          </button>
          <div className="flex flex-1 items-center gap-1.5 rounded-lg border border-border bg-card px-2">
            <Search size={14} className="text-muted-foreground" />
            <input ref={searchRef} value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doSearch()}
              placeholder="Buscar…" className="w-full bg-transparent py-1.5 text-sm text-foreground outline-none placeholder:text-muted-foreground" />
          </div>
          {view === 'mail' && folder === 'INBOX' && (
            <button onClick={toggleGrouped} aria-pressed={grouped}
              className={`rounded-lg border p-1.5 hover:bg-muted ${grouped ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card text-foreground'}`}
              title={grouped ? 'Ver correo por correo' : 'Agrupar por conversación'}>
              <MessagesSquare size={14} />
            </button>
          )}
          <button onClick={refresh} className="rounded-lg border border-border bg-card p-1.5 text-foreground hover:bg-muted" title="Actualizar">
            <RefreshCw size={14} className={loadingList ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Pestañas por etiqueta (banderitas) — scroll horizontal en móvil */}
        {view === 'mail' && availableLabels.length > 0 && (
          <div className="flex items-center gap-1.5 overflow-x-auto border-b border-border px-3 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <button onClick={() => selectLabel('')}
              className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-medium transition ${
                labelFilter === '' ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card text-muted-foreground hover:bg-muted'}`}>
              Todos
            </button>
            {availableLabels.map((l) => (
              <button key={l} onClick={() => selectLabel(l)}
                className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition ${
                  labelFilter === l ? labelColor(l) + ' ring-1 ring-current' : 'border-border bg-card text-muted-foreground hover:bg-muted'}`}>
                <Tag size={11} /> {l}
              </button>
            ))}
          </div>
        )}

        {error && <div className="border-b border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}

        <div className="min-h-0 flex-1 overflow-y-auto">
          {view === 'clients' && <ClientsPane onOpenUid={openUid} />}
          {view === 'mail' && loadingList && <div className="p-6 text-center text-sm text-muted-foreground">Cargando…</div>}
          {view === 'mail' && !loadingList && list.length === 0 && (
            <div className="p-10 text-center text-muted-foreground">
              <Mail size={28} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">Sin correos en esta carpeta.</p>
            </div>
          )}
          {view === 'mail' && rows.map((m) => (
            <button key={m.uid} onClick={() => openMessage(m)}
              className={`flex w-full items-start gap-2 border-b border-border/60 px-3 py-2.5 text-left hover:bg-muted/50 ${
                selected?.uid === m.uid ? 'bg-muted/70' : ''} ${!m.seen ? 'bg-primary/[0.04]' : ''}`}>
              <span onClick={(e) => { e.stopPropagation(); void onStar(m); }}
                className={`mt-0.5 shrink-0 ${m.flagged ? 'text-amber-500' : 'text-muted-foreground/40 hover:text-amber-500'}`}>
                <Star size={14} fill={m.flagged ? 'currentColor' : 'none'} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className={`truncate text-sm ${!m.seen ? 'font-semibold text-foreground' : 'text-foreground/80'}`}>
                    {m.from_name || m.from_addr || '(desconocido)'}
                  </span>
                  {m.importance && (
                    <span className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold ${IMP_BADGE[m.importance].cls}`}>
                      {IMP_BADGE[m.importance].label}
                    </span>
                  )}
                  <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">{fmtDate(m.date)}</span>
                </div>
                <div className={`truncate text-sm ${!m.seen ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                  {m.subject || '(sin asunto)'}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                  {m.has_attachments && <Paperclip size={11} />}
                  {m.answered && <CornerUpLeft size={11} />}
                  {folder === 'INBOX' && (triage[m.uid]?.threadCount ?? 0) > 1 && (
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-medium"
                      title="Correos en la conversación">
                      <MessagesSquare size={9} /> {triage[m.uid].threadCount}
                    </span>
                  )}
                  {folder === 'INBOX' && triage[m.uid]?.clientName && (
                    <span className="max-w-[9rem] truncate rounded-full border border-border px-1.5 py-0.5 text-[9px]"
                      title="Cliente">{triage[m.uid].clientName}</span>
                  )}
                  {folder === 'INBOX' && triage[m.uid]?.forwardStatus === 'sent' && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] text-primary" title={`Reenviado a ${triage[m.uid].forwardedTo}`}>
                      <Forward size={9} /> reenviado
                    </span>
                  )}
                  {(m.labels ?? []).map((l) => (
                    <span key={l} className={`inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[9px] font-medium ${labelColor(l)}`}>
                      <Tag size={8} /> {l}
                    </span>
                  ))}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Paginación */}
        {view === 'mail' && total > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t border-border px-3 py-2 text-xs text-muted-foreground">
            <span>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} de {total}</span>
            <div className="flex gap-1">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                className="rounded border border-border p-1 disabled:opacity-40 hover:bg-muted"><ChevronLeft size={14} /></button>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
                className="rounded border border-border p-1 disabled:opacity-40 hover:bg-muted"><ChevronRight size={14} /></button>
            </div>
          </div>
        )}
      </section>

      {/* ── Lector ── */}
      <section className={`${selected ? 'flex' : 'hidden md:flex'} min-w-0 flex-1 flex-col bg-background`}>
        {!selected && !loadingMsg && (
          <div className="flex flex-1 flex-col items-center justify-center text-muted-foreground">
            <MailOpen size={40} className="mb-3 opacity-30" />
            <p className="text-sm">Selecciona un correo para leerlo.</p>
          </div>
        )}
        {loadingMsg && <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">Abriendo…</div>}
        {selected && (
          <>
            {/* toolbar */}
            <div className="flex items-center gap-1.5 border-b border-border px-3 py-2">
              <button onClick={() => setSelected(null)} className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted md:hidden">
                <ChevronLeft size={16} />
              </button>
              <button onClick={toggleWide}
                className="hidden rounded-lg border border-border bg-card p-1.5 text-muted-foreground hover:bg-muted md:inline-flex"
                title={wide ? 'Volver a la vista con lista' : 'Leer a pantalla completa'}
                aria-label={wide ? 'Volver a la vista con lista' : 'Leer a pantalla completa'}>
                {wide ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
              </button>
              <button onClick={() => setCompose({ to: selected.from_addr, subject: selected.subject.toLowerCase().startsWith('re:') ? selected.subject : `Re: ${selected.subject}`, body: quote(selected), inReplyTo: selected.message_id })}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-sm text-foreground hover:bg-muted">
                <CornerUpLeft size={14} /> Responder
              </button>
              <button onClick={() => setCompose({ subject: `Fwd: ${selected.subject}`, body: quote(selected) })}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-sm text-foreground hover:bg-muted">
                <Forward size={14} /> Reenviar
              </button>
              <div className="relative">
                <button onClick={() => setMoveOpen((v) => !v)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-sm text-foreground hover:bg-muted">
                  Mover
                </button>
                {moveOpen && (
                  <div className="absolute z-10 mt-1 w-40 rounded-lg border border-border bg-card py-1 shadow-lg">
                    {folders.filter((f) => f.name !== folder).map((f) => (
                      <button key={f.name} onClick={() => onMove(f.name)}
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-foreground hover:bg-muted">
                        {FOLDER_ICON[f.role] ?? FOLDER_ICON.custom} {f.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="relative">
                <button onClick={() => setLabelMenuOpen((v) => !v)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-sm text-foreground hover:bg-muted" title="Etiquetas">
                  <Tag size={14} /> <span className="hidden sm:inline">Etiquetas</span>
                </button>
                {labelMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setLabelMenuOpen(false)} aria-hidden />
                    <div className="absolute z-20 mt-1 w-64 rounded-lg border border-border bg-card p-2 shadow-lg">
                      {(selected.labels ?? []).length > 0 && (
                        <div className="mb-2">
                          <div className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">En este correo</div>
                          <div className="flex flex-wrap gap-1">
                            {(selected.labels ?? []).map((l) => (
                              <button key={l} onClick={() => void onLabelToggle(l, false)}
                                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${labelColor(l)}`} title="Quitar etiqueta">
                                {l} <X size={10} />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Agregar</div>
                      <div className="mb-2 flex flex-wrap gap-1">
                        {DEFAULT_LABELS.filter((l) => !(selected.labels ?? []).some((x) => x.toLowerCase() === l.toLowerCase())).map((l) => (
                          <button key={l} onClick={() => void onLabelToggle(l, true)}
                            className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted">
                            <Plus size={10} /> {l}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-1">
                        <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter' && newLabel.trim()) void onLabelToggle(newLabel, true); }}
                          placeholder="Nueva etiqueta…"
                          className="w-full rounded-lg border border-border bg-card px-2 py-1 text-xs text-foreground outline-none placeholder:text-muted-foreground" />
                        <button onClick={() => { if (newLabel.trim()) void onLabelToggle(newLabel, true); }}
                          className="shrink-0 rounded-lg bg-primary px-2 py-1.5 text-primary-foreground hover:opacity-90" title="Crear y aplicar">
                          <Plus size={12} />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <button onClick={() => { const e = list.find((x) => x.uid === selected.uid); if (e) void onMarkUnread(e); }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-sm text-foreground hover:bg-muted" title="Marcar como no leído">
                <Mail size={14} />
              </button>
              {currentRole === 'junk' ? (
                <button onClick={onNotSpam}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-sm text-foreground hover:bg-muted" title="No es spam — mover a la bandeja">
                  <Inbox size={14} /> No es spam
                </button>
              ) : hasJunk && (
                <button onClick={onSpam}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-sm text-foreground hover:bg-muted" title="Marcar como spam">
                  <AlertOctagon size={14} /> Spam
                </button>
              )}
              <button onClick={() => { const e = list.find((x) => x.uid === selected.uid) || ({ uid: selected.uid } as MailEnvelope); void onDelete(e); }}
                className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-sm text-red-600 hover:bg-red-50">
                <Trash2 size={14} /> Borrar
              </button>
            </div>

            {/* encabezado */}
            <div className="border-b border-border px-5 py-4">
              <h1 className="mb-2 text-lg font-semibold text-foreground">{selected.subject || '(sin asunto)'}</h1>
              <div className="flex items-center gap-2 text-sm">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  {(selected.from_name || selected.from_addr || '?').slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="truncate font-medium text-foreground">{selected.from_name || selected.from_addr}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {selected.from_name ? `${selected.from_addr} · ` : ''}para {selected.to || 'ti'}
                  </div>
                </div>
                <span className="ml-auto shrink-0 text-xs text-muted-foreground">{fmtFull(selected.date)}</span>
              </div>
              {(selected.labels ?? []).length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1">
                  {(selected.labels ?? []).map((l) => (
                    <span key={l} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${labelColor(l)}`}>
                      <Tag size={10} /> {l}
                      <button onClick={() => void onLabelToggle(l, false)} className="ml-0.5 opacity-60 hover:opacity-100" title="Quitar"><X size={10} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* cuerpo — con historial de conversación colapsable */}
            <div className={`min-h-0 flex-1 overflow-y-auto px-5 py-4 ${reading ? "lg:px-12" : ""}`}>
              {(() => {
                const t = selected.folder === 'INBOX' ? triage[selected.uid] : undefined;
                if (!t || (!t.summary && !t.draft && !t.reason)) return null;
                return (
                  <div className="mb-4 rounded-xl border border-primary/25 bg-primary/[.04] p-3">
                    <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-primary">
                      <Sparkles size={13} /> Resumen IA
                      {t.importance && (
                        <span className={`ml-1 rounded-full border px-1.5 py-0.5 text-[9px] ${IMP_BADGE[t.importance].cls}`}>
                          {IMP_BADGE[t.importance].label}
                        </span>
                      )}
                    </div>
                    {t.summary && <p className="text-sm leading-relaxed text-foreground/90">{t.summary}</p>}
                    {t.reason && (
                      <p className="mt-1 text-xs text-muted-foreground"><span className="font-medium">Por qué:</span> {t.reason}</p>
                    )}
                    {t.forwardStatus === 'sent' && (
                      <p className="mt-1 inline-flex items-center gap-1 text-xs text-primary">
                        <Forward size={11} /> Reenviado automáticamente a {t.forwardedTo}
                      </p>
                    )}
                    {t.draft && (
                      <button
                        onClick={() => setCompose({
                          to: selected.from_addr,
                          subject: selected.subject.toLowerCase().startsWith('re:') ? selected.subject : `Re: ${selected.subject}`,
                          body: t.draft,
                          inReplyTo: selected.message_id,
                        })}
                        className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"
                      >
                        <CornerUpLeft size={12} /> Usar borrador IA
                      </button>
                    )}
                  </div>
                );
              })()}
              {(() => {
                const t = selected.folder === 'INBOX' ? triage[selected.uid] : undefined;
                if (!t?.threadKey || t.threadCount < 2) return null;
                return <ThreadBox threadKey={t.threadKey} count={t.threadCount} summary={t.threadSummary}
                  currentUid={selected.uid} onOpenUid={openUid} />;
              })()}
              <MessageBody message={selected} />

              {selected.attachments.length > 0 && (
                <div className="mt-5 border-t border-border pt-3">
                  <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <Paperclip size={12} /> {selected.attachments.length} adjunto(s)
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selected.attachments.map((a) => (
                      <button key={a.idx} onClick={() => downloadAttachment(selected.folder, selected.uid, a.idx, a.name)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs text-foreground hover:bg-muted">
                        <Download size={12} /> {a.name}
                        {a.is_pdf && <span className="text-[10px] text-primary">PDF</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </section>

      {compose && <ComposeModal preset={compose} onClose={() => setCompose(null)} onSent={refresh} />}
    </div>
  );
}
